export interface Localized {
    [key: string]: {
        name?: string
        text?: string
    }
}

export interface Link {
    id: string
    url: string
    text: string
    textDe: string
    urlDe: string
    order: number
}

export interface Organization {
    id: string
    name: string
    industry: string
    city: string
    address1: string
    address2: string
    address3: string
    logoUrl: string
    logoFileId?: string
    backgroundImageUrl: string
    backgroundImageFileId?: string
    backgroundImageMobileUrl: string
    backgroundImageMobileFileId?: string
    mobile: string
    phone: string
    fax: string
    email: string
    email2: string
    postCode: string
    countryCode: string
    facebook: string
    instagram: string
    linkedIn: string
    twitter: string
    tiktok: string
    pinterest: string
    xing: string
    youTube: string
    foundingYear: string
    size: string
    web: string
    description: string
    teaser: string
    descriptionDe: string
    teaserDe: string
    tags: string[]
    headerVideoUrl: string
    headerVideoFileId?: string
    headerVideoImageUrl: string
    headerVideoImageFileId?: string
    stands: Stand[]
    iframeUrl: string
    iframeUrlDe: string
    consentTitle: string
    consentTitleDe: string
    consentText: string
    consentTextDe: string
    links: Link[]
    categories: Category[]
    products: Product[]
    productsForCollections: Product[]
    trademarks: Trademark[]
    persons: Person[]
    medias: Media[]
    newsList: News[]
    exhibitorNewsList: News[]
    organizerNewsList: News[]
    storyNewsList: News[]
    eventDates: EventDate[]
    showrooms: Showrooms[]
    goodies: Coupons[]
    notificationPromotions: Coupons[]
    jobOffers: Job[]
    printCatalog: PrintCatalog
    sortName: string
    notificationTemplates: NotificationTemplate[]
}

export interface Category {
    id: string
}

export interface Stand {
    id: string
    standNr: string
    standName: string
    standNameDe: string
}

export interface Properties {
    id: string
    name: string
    nameDe: string
    productType: string
    type: string
    unit: string
    unitDe: string
    val: string
    enumId: string
}

export interface Product {
    id: string
    name: string
    nameDe: string
    initials: string
    orderProduct: number
    showFrom: Date | undefined
    showUntil: Date | undefined
    crossValidation: undefined
    description: string
    teaser: string
    descriptionDe: string
    teaserDe: string
    logoUrl: string
    logoFileId?: string
    backgroundImageUrl: string
    backgroundImageFileId?: string
    tags: string[]
    medias: Media[]
    categories: Category[]
    links: Link[]
    type: string
    property: string
    properties: Properties[]
    //used only for validation purposes
    collection: string | null
}

export interface Trademark {
    id: string
    name: string
    initials: string
    orderTrademark: number
    description: string
    teaser: string
    descriptionDe: string
    teaserDe: string
    logoUrl: string
    logoFileId?: string
    tags: string[]
    medias: Media[]
    categories: Category[]
}

export interface Tags {
    tag: string
}

export interface Media {
    id: string
    fileId: string
    url: string
    name: string
    contentType: string
    size: string
    order: number
    title: string
    titleDe: string
    subtitle: string
    subtitleDe: string
}

export interface Person {
    id: string
    isEditable: boolean
    title: string
    firstName: string
    lastName: string
    salutation: string
    midName: string
    initials: string
    function: string
    company: string
    position: string
    positionDe: string
    email: string
    phone: string
    fax: string
    web: string
    logoUrl: string
    logoFileId?: string
    orderPerson: number
    description: string
    teaser: string
    descriptionDe: string
    teaserDe: string
    emailVisible: boolean
    phoneVisible: boolean
}

export type NewsType = "ORGANIZATION" | "EVENTORGANIZER" | "STORY"

export interface News {
    id: string
    name: string
    nameDe: string
    logoUrl: string
    initials: string
    date?: Date
    description: string
    descriptionDe: string
    teaser: string
    teaserDe: string
    logoFileId?: string
    tags: string[]
    medias: Media[]
    categories: Category[]
    suppressOrganizationLinkage: boolean
    type?: NewsType
}

export interface EventDate {
    id: string
    orderEventDates: number
    name: string
    nameDe: string
    logoFileId?: string
    logourl: string
    logourlDe: string
    startdatetime: string
    enddatetime: string
    eventName: string
    eventNameDe: string
    location: string
    locationDe: string
    type: string
    isListable: boolean
    liveStreamingPreviewUrl: string
    liveStreamingPreviewFileID?: string
    breakoutAccess: boolean
    description: string
    teaser: string
    descriptionDe: string
    teaserDe: string
    persons: Person[]
    medias: Media[]
    tags: string[]
}

export interface Showrooms {
    id: string
    orderShowrooms: number
    title: string
    titleDe: string
    logoFileId?: string
    logourl: string
    logourlDe: string
    accessMethod: string
    shortDescription: string
    shortDescriptionDe: string
}

export interface Coupons {
    id: string
    name: string
    nameDe: string
    url: string
    urlDe: string
    logoUrl: string
    logoFileId?: string
    descriptionTitle: string
    descriptionTitleDe: string
    descriptionLong: string
    descriptionLongDe: string
    descriptionShort: string
    descriptionShortDe: string
    validStart: Date
    validEnd: Date
    isVisible: boolean
}

export interface Job {
    id: string
    title: string
    titleDe: string
    logoFileId?: string
    logoUrl: string
    backgroundImageFileId?: string
    backgroundImageUrl: string
    location: string
    locationDe: string
    employment: string
    employmentDe: string
    careerLevel: string
    careerLevelDe: string
    salaryGroup: string
    requirements: string
    requirementsDe: string
    active: boolean
    links: Link[]
    startdate?: Date
    updated?: Date
    showFrom?: Date
    showUntil?: Date
    description: string
    descriptionDe: string
    homeOffice: string
    persons: Person[]
    medias: Media[]
    categories: Category[]
}

export interface NotificationTemplate {
    id: string
    subelementPackage?: "notificationTemplate#small" | "notificationTemplate#medium" | "notificationTemplate#large"
    text: string
    trigger: NotificationTemplateTrigger
    maxUsageCount: number
    destinations: NotificationTemplateDestination[]
    targetGroup: NotificationTemplateTargetGroup
    action: NotificationTemplateAction
    localized: Localized
    selector?: NotificationTemplateSelector

    //properties for temporary usage by the frontend
    date?: string
    coupon?: Coupons
}

export interface NotificationTemplateSelector {
    geofences: NotificationTemplateGeofence[]
    geofenceDestination?: string
    timeStart: string
    timeEnd: string
    questionFilters: string[]
}

export interface NotificationTemplateGeofence {
    id: string
}

export interface NotificationTemplateDestination {
    id: string
}

export interface NotificationTemplateFilter {
    allow: string[]
    deny: []
}

export interface NotificationTemplateTrigger {
    type: string
    geofences: NotificationTemplateGeofence[]
    timeFrameStart: string
    timeFrameEnd: string
}

export interface NotificationTemplateTargetGroup {
    interests?: TargetGroupInterest[]
    countriesFilter?: TargetGroupCountry
    questionFilters?: TargetGroupQuestion[]
}

export interface TargetGroupElement {
    id: string
}

export interface TargetGroupInterest {
    id: string
    credits: number
}

export interface TargetGroupCountry {
    type: "include" | "exclude"
    values: string[]
}

export interface TargetGroupQuestion {
    questionId: string
    answerIds: string[]
}
export interface NotificationTemplateAction {
    type: string
    param: string
}

export interface PrintAdPage {
    id: string
    adUrl: string
    adFileId?: string
    name: string
}
export interface PrintCatalog {
    id: string

    logoUrl: string
    logoFileId?: string
    fileType: string

    nameImported: boolean
    name: string

    addressImported: boolean
    address1: string
    address2: string
    address3: string

    postcode: string
    city: string
    countryCode: string

    contactImported: boolean
    phone: string
    fax: string
    email: string
    web: string

    descriptionImported: boolean
    description: string
    descriptionDe: string

    adPages: Array<PrintAdPage>
}

export interface ExhibitorData {
    id: string
    token: string
    organization: Organization
    organizationBackup: Organization
}

export interface UploadResult {
    fileId: string
    fileUrl: string
    fileName: string
    contentType: string
    size: string
}

export enum QrCodeEntityType {
    ORGANIZATION = "organization",
    PRODUCT = "product",
    TRADEMARK = "trademark",
    JOBOFFER = "joboffer",
    NEWS = "news",
    EVENTDATE = "eventdate"
}

export enum QrCodeContentType {
    CUSTOM_PROTOCOL = "CUSTOM_PROTOCOL",
    ACTION_TYPE_AND_PARAM = "ACTION_TYPE_AND_PARAM",
    UNIVERSAL_LINK = "UNIVERSAL_LINK"
}

export enum QrCodeFormat {
    SVG = "svg",
    PNG = "png"
}

export interface QrCodeResult {
    entityType: QrCodeEntityType
    entityid: string
    entityName: string
    contentType: QrCodeContentType
    content: string
    url: string
    format: QrCodeFormat
}
